// CHG-1325 / ITER-3452 — Tag taxonomy phase 3 (FE integration).
// CHG-1334 / ITER-3461 — DRIFT-649 closeout: inverse-lookup wired to real
//   GET /api/v1/tags/{tag_id}/applications endpoint.
//
// REAL fetches against the T4 backend (9 endpoints declared in
// specs/tier-04-interface/tag-management.api.yaml v0.2.0). The page
// surfaces shipped with the v2 canary (Tags + TagDetail) and the
// entity-detail "Tags" tabs that consume EntityTagsSection + TagPicker
// hit real endpoints.
//
// Endpoint map (all rooted at base_url=/api/v1):
//   listTags             → GET    /tags
//   getTag               → GET    /tags/{tag_id}
//   createTag            → POST   /tags
//   updateTag            → PATCH  /tags/{tag_id}
//   deleteTag            → DELETE /tags/{tag_id}
//   attachTag            → POST   /{entity_type}/{entity_id}/tags
//   detachTag            → DELETE /{entity_type}/{entity_id}/tags/{tag_id}
//   listEntityTags       → GET    /{entity_type}/{entity_id}/tags
//   listTagApplications  → GET    /tags/{tag_id}/applications (CHG-1334)
//
// CHG-1334 closes DRIFT-649: the per-tab inverse-lookup helpers
// (listTagDonors / listTagCampaigns / listTagRecurring / listTagPledges
// + listTagDonations + listTagMessages) now dispatch to the real
// listTagApplications endpoint parameterized by entity_type, instead
// of the previous safeFetch empty-envelope fallback.

import { apiClient } from "./client";
import type {
  EntityTag,
  EntityTagWithTag,
  Tag,
  TagApplicationListResponse,
  TagListResponse,
} from "./types";

async function safeFetch<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch {
    return fallback;
  }
}

// ─────────────────────────────────────────────────────────────────
// Tag CRUD
// ─────────────────────────────────────────────────────────────────

export interface ListTagsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  applied_to_type?: string;
}

/**
 * GET /api/v1/tags — paginated tag list scoped to the active org.
 *
 * The T4 contract returns cursor pagination; the FE ListPage uses
 * offset/limit-style page numbers. We translate page→cursor by passing
 * the last seen cursor token; first page omits cursor.
 */
export async function listTags(
  params: ListTagsParams = {},
): Promise<{ items: Tag[]; total: number }> {
  const pageSize = params.pageSize ?? 50;
  const r = await apiClient().get<TagListResponse>("/tags", {
    params: {
      limit: pageSize,
      search: params.search || undefined,
      applied_to_type: params.applied_to_type || undefined,
    },
  });
  return { items: r.data.items, total: r.data.total_count };
}

export async function getTag(id: string): Promise<Tag | null> {
  return safeFetch(
    async () => {
      const r = await apiClient().get<Tag>(`/tags/${id}`);
      return r.data;
    },
    null,
  );
}

export interface CreateTagBody {
  name: string;
  color: string;
  description?: string | null;
}

export async function createTag(body: CreateTagBody): Promise<Tag> {
  const r = await apiClient().post<Tag>("/tags", body);
  return r.data;
}

export interface UpdateTagPatch {
  name?: string;
  color?: string;
  description?: string | null;
}

export async function updateTag(
  id: string,
  patch: UpdateTagPatch,
): Promise<Tag> {
  const r = await apiClient().patch<Tag>(`/tags/${id}`, patch);
  return r.data;
}

export async function deleteTag(id: string): Promise<void> {
  await apiClient().delete(`/tags/${id}`);
}

// ─────────────────────────────────────────────────────────────────
// Polymorphic attach / detach / list-per-entity
// ─────────────────────────────────────────────────────────────────

export type TaggableEntityType =
  | "donor"
  | "campaign"
  | "recurring_donation"
  | "pledge"
  | "donation"
  | "message";

export const TAGGABLE_ENTITY_TYPES: readonly TaggableEntityType[] = [
  "donor",
  "campaign",
  "recurring_donation",
  "pledge",
  "donation",
  "message",
];

/**
 * POST /api/v1/{entity_type}/{entity_id}/tags — attach an existing tag.
 *
 * Body carries only tag_id; entity_type + entity_id come from the URL
 * per the T4 contract (CHG-1311 EntityTagAttachRequest schema). Returns
 * the full EntityTagWithTag (denormalized Tag included so the caller
 * can render the new chip without a follow-up fetch).
 */
export async function attachTag(
  entityType: TaggableEntityType,
  entityId: string,
  tagId: string,
): Promise<EntityTagWithTag> {
  const r = await apiClient().post<EntityTagWithTag>(
    `/${entityType}/${entityId}/tags`,
    { tag_id: tagId },
  );
  return r.data;
}

/**
 * DELETE /api/v1/{entity_type}/{entity_id}/tags/{tag_id} — detach.
 *
 * 404 if the (tag, entity) pair was never attached or the tag does
 * not belong to the active org.
 */
export async function detachTag(
  entityType: TaggableEntityType,
  entityId: string,
  tagId: string,
): Promise<void> {
  await apiClient().delete(`/${entityType}/${entityId}/tags/${tagId}`);
}

/**
 * GET /api/v1/{entity_type}/{entity_id}/tags — tags applied to one entity.
 *
 * Used by EntityTagsSection on every taggable-entity detail page. The
 * T4 contract returns EntityTagListResponse with items pre-joined to
 * TagRead so the consumer can render chips without N+1 fetches.
 */
export async function listEntityTags(
  entityType: TaggableEntityType,
  entityId: string,
): Promise<EntityTagWithTag[]> {
  const r = await apiClient().get<{
    items: EntityTagWithTag[];
    total_count: number;
  }>(`/${entityType}/${entityId}/tags`);
  return r.data.items;
}

// ─────────────────────────────────────────────────────────────────
// Inverse lookup — entities with a given tag (CHG-1334 / DRIFT-649)
//
// CHG-1334 shipped GET /api/v1/tags/{tag_id}/applications which
// returns a uniform TagApplication envelope across all six canonical
// entity_types. The per-tab helpers below dispatch to it with the
// matching `entity_type` query param. They preserve the page=N /
// pageSize=N consumer signature used by the tag-detail page so the
// UI doesn't need to change call sites.
// ─────────────────────────────────────────────────────────────────

export interface ListTagApplicationsParams {
  entity_type?: TaggableEntityType;
  cursor?: string;
  limit?: number;
}

/**
 * GET /api/v1/tags/{tag_id}/applications — inverse lookup.
 *
 * Returns a cursor-paginated list of entities tagged with `tagId`.
 * Optionally filter to one entity_type. Mirrors the T4 spec at
 * specs/tier-04-interface/tag-management.api.yaml#listTagApplications.
 */
export async function listTagApplications(
  tagId: string,
  params: ListTagApplicationsParams = {},
): Promise<TagApplicationListResponse> {
  const r = await apiClient().get<TagApplicationListResponse>(
    `/tags/${tagId}/applications`,
    {
      params: {
        entity_type: params.entity_type || undefined,
        cursor: params.cursor || undefined,
        limit: params.limit ?? undefined,
      },
    },
  );
  return r.data;
}

// ─── Per-tab convenience helpers ───────────────────────────────
// Each wraps listTagApplications with a fixed entity_type. Returns
// safeFetch-style empty list on transport error so the tag-detail
// tabs don't blank out the whole page when the backend is
// momentarily unreachable.

async function _listByType(
  tagId: string,
  entityType: TaggableEntityType,
): Promise<TagApplicationListResponse> {
  return safeFetch(
    () => listTagApplications(tagId, { entity_type: entityType }),
    { items: [], next_cursor: null, total_count: 0 },
  );
}

export async function listTagDonors(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "donor");
}

export async function listTagCampaigns(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "campaign");
}

export async function listTagRecurring(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "recurring_donation");
}

export async function listTagPledges(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "pledge");
}

export async function listTagDonations(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "donation");
}

export async function listTagMessages(
  id: string,
): Promise<TagApplicationListResponse> {
  return _listByType(id, "message");
}

// ─────────────────────────────────────────────────────────────────
// Bundle export — consumer surface
// ─────────────────────────────────────────────────────────────────

export const tagsApi = {
  // Tag CRUD
  listTags,
  getTag,
  createTag,
  updateTag,
  deleteTag,
  // Polymorphic attach / detach / list
  attachTag,
  detachTag,
  listEntityTags,
  // Inverse lookup (CHG-1334)
  listTagApplications,
  listTagDonors,
  listTagCampaigns,
  listTagRecurring,
  listTagPledges,
  listTagDonations,
  listTagMessages,
};

// Re-export the EntityTag/EntityTagWithTag types so consumers
// (TagPicker, EntityTagsSection) don't have to import from ./types.
export type { EntityTag, EntityTagWithTag };
